// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TagChip } from './TagChip';
import './TagChipGroup.css';

export interface TagChipGroupTag {
  id: string;
  name: string;
  color: string;
  archived?: boolean;
}

export interface TagChipGroupProps {
  tags: TagChipGroupTag[];
  /** Number of chips rendered before collapsing the rest into a "+k" popover. Defaults to 3 (PRD §9). */
  max?: number;
  className?: string;
  'data-testid'?: string;
}

/**
 * Renders the first `max` tags as chips, then a "+k" overflow trigger that
 * opens a popover listing the remaining tags. Read-only (no remove
 * affordance) — used for list-row / detail-header display; editing happens
 * through {@link TagPicker}.
 */
export function TagChipGroup({ tags, max = 3, className = '', 'data-testid': dataTestId }: TagChipGroupProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition({ top: rect.bottom + 4, left: rect.left });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (tags.length === 0) return null;

  const visible = tags.slice(0, max);
  const overflow = tags.slice(max);

  const popover = open && position ? createPortal(
    <div
      ref={popoverRef}
      className="fin-tag-chip-group__popover"
      role="listbox"
      style={{ top: position.top, left: position.left }}
    >
      {overflow.map((tag) => (
        <TagChip key={tag.id} name={tag.name} color={tag.color} archived={tag.archived} />
      ))}
    </div>,
    document.body,
  ) : null;

  return (
    <div className={`fin-tag-chip-group ${className}`.trim()} data-testid={dataTestId}>
      {visible.map((tag) => (
        <TagChip key={tag.id} name={tag.name} color={tag.color} archived={tag.archived} />
      ))}
      {overflow.length > 0 && (
        <>
          <button
            ref={triggerRef}
            type="button"
            className="fin-tag-chip-group__overflow"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`${overflow.length} more tags`}
            onClick={() => setOpen((o) => !o)}
          >
            +{overflow.length}
          </button>
          {popover}
        </>
      )}
    </div>
  );
}
